// Base query with JWT authentication and error handling

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getApiBaseUrl } from '@/utils/env';

/**
 * Get the current access token from localStorage using the auth_tokens key
 */
const getAccessToken = (): string | null => {
  try {
    const tokens = localStorage.getItem('auth_tokens');
    if (tokens) {
      const parsedTokens = JSON.parse(tokens);
      return parsedTokens.accessToken || null;
    }
    return null;
  } catch (error) {
    console.error('Failed to get access token:', error);
    return null;
  }
};

// Create base query with JWT authentication
const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  prepareHeaders: (headers) => {
    // Add JWT authentication token if available
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Add standard headers that work well with CORS
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');

    return headers;
  },
  // Add fetch options to handle CORS properly
  fetchFn: async (input, init) => {
    // Ensure proper CORS handling
    const enhancedInit = {
      ...init,
      mode: 'cors' as RequestMode,
      credentials: 'omit' as RequestCredentials, // Don't send cookies unless needed
    };

    return fetch(input, enhancedInit);
  },
});

export const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // Handle 401 Unauthorized
  if (result.error && result.error.status === 401) {
    console.error('Authentication required - JWT token may be expired or invalid');
    window.dispatchEvent(new Event('auth-error'));
  }

  // Handle other common errors
  if (result.error) {
    switch (result.error.status) {
      case 403:
        console.error('Access forbidden - insufficient permissions');
        break;
      case 404:
        console.error('Resource not found');
        break;
      case 500:
        console.error('Internal server error');
        break;
      default:
        console.error('API Error:', result.error);
    }
  }

  return result;
};
